import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Rnd, RndResizeCallback } from 'react-rnd'
import { Box } from '@mui/material'

import { getTabHeight as getTabMinHeight } from '@components/DisplayForm/helpers'

import { FLEXIBLE, FORM_CONTAINER_TAB_ID, MIN_COLLAPSED_LC_HEIGHT } from '@constants'

import { FormDataEnriched, FormTab } from '../../../../types'
import { LinesWithElements } from '../LinesWithElements/LinesWithElements'

type ContainerTabPanelProps = {
  tab: FormTab<FormDataEnriched>
  initialHeight: number
  contentHeight: number
  isDialogWindow: boolean
}

export const ContainerTabPanel: FC<ContainerTabPanelProps> = ({
  tab,
  initialHeight,
  isDialogWindow,
  contentHeight,
}) => {
  const tabRef = useRef()
  const [resizeHeightTab, setResizeHeightTab] = useState(initialHeight)
  const [listControlStates, setListControlStates] = useState<
    Record<number, { expanded: boolean; height: number }>
  >({})
  const tabMinHeight = useMemo(() => {
    return getTabMinHeight(tab, isDialogWindow, listControlStates)
  }, [tab, isDialogWindow, listControlStates])

  useEffect(() => {
    const hasExpandedView = Object.values(listControlStates).some(value => value.expanded)

    const totalHeight =
      hasExpandedView && tabMinHeight < contentHeight ? contentHeight : tabMinHeight
    if (totalHeight !== resizeHeightTab) {
      setResizeHeightTab(totalHeight)
    }
  }, [tabMinHeight])

  useEffect(() => {
    if (tabRef.current && tabRef.current.scrollTop !== 0) {
      tabRef.current.scrollTop = 0
    }
  }, [tabRef.current])

  const handleResizeStop: RndResizeCallback = (_e, _direction, ref) => {
    const result = parseInt(ref.style.height, 10)

    if (result >= MIN_COLLAPSED_LC_HEIGHT) {
      setResizeHeightTab(result)
    }
  }

  return (
    <Rnd
      disableDragging
      enableResizing={{ bottom: true }}
      minHeight={tabMinHeight}
      resizeHandleStyles={{ bottom: { bottom: -10 } }}
      size={{ height: resizeHeightTab, width: '100%' }}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
      onResizeStop={handleResizeStop}
    >
      <Box
        ref={tabRef}
        id={`${FORM_CONTAINER_TAB_ID}_${tab.id}`}
        sx={theme => ({
          ...FLEXIBLE,
          background: theme.palette.background.formCardContainerContent,
          m: 0,
          p: 2,
          overflowY: 'auto',
        })}
      >
        <LinesWithElements
          lines={tab.lines}
          listControlStates={listControlStates}
          setListControlStates={setListControlStates}
          tabId={tab.id}
        />
      </Box>
    </Rnd>
  )
}
